import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import {
  Brain, Sparkles, Clock, Phone, Mail, Linkedin, Calendar,
  AlertCircle, CheckCircle2, X, RefreshCw, Download, Settings,
  ChevronRight, Zap, Target, Shield, AlertTriangle,
  Info, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const URGENCY_COLORS = {
  high: 'bg-red-500',
  medium: 'bg-amber-500',
  low: 'bg-emerald-500'
};

const METHOD_ICONS = {
  email: Mail,
  phone: Phone,
  linkedin: Linkedin,
  meeting: Calendar,
  other: Target
};

export const FollowUpRemindersPanel = ({ onRefresh }) => {
  const { token, user } = useAuth();
  const [reminders, setReminders] = useState([]);
  const [staleContacts, setStaleContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [recommendation, setRecommendation] = useState(null);
  const [loadingRec, setLoadingRec] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [usageStats, setUsageStats] = useState(null);
  
  // Mobile collapse state - collapsed by default on mobile
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const isDirector = user?.role === 'director';

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [remindersRes, staleRes, usageRes] = await Promise.all([
        fetch(`${API_URL}/api/followup/reminders`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/followup/stale-contacts?days=7`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/followup/usage`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (remindersRes.ok) {
        const data = await remindersRes.json();
        setReminders(data.reminders || []);
      }

      if (staleRes.ok) {
        const data = await staleRes.json();
        setStaleContacts(data.contacts || []);
      }

      if (usageRes.ok) {
        const data = await usageRes.json();
        setUsageStats(data.usage);
      }
    } catch (err) {
      console.error('Error fetching follow-up data:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchSettings = async () => {
    if (!token || !isDirector) return;
    try {
      const res = await fetch(`${API_URL}/api/followup/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSettings(data.settings);
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
    }
  };

  const saveSettings = async () => {
    if (!token || !settings) return;
    setSavingSettings(true);
    try {
      const res = await fetch(`${API_URL}/api/followup/settings`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      if (res.ok) {
        toast.success('Follow-up settings saved');
        setSettingsOpen(false);
        fetchData(); // Refresh usage stats
      } else {
        toast.error('Failed to save settings');
      }
    } catch (err) {
      toast.error('Error saving settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const getRecommendation = async (contactId) => {
    if (!token) return;
    setLoadingRec(true);
    try {
      const res = await fetch(`${API_URL}/api/followup/recommendation/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRecommendation(data);
        setSelectedContact(data.contact);
        fetchData(); // Refresh usage stats after AI call
      } else {
        toast.error('Failed to get recommendation');
      }
    } catch (err) {
      toast.error('Error getting recommendation');
    } finally {
      setLoadingRec(false);
    }
  };

  const dismissReminder = async (reminderId) => {
    try {
      const res = await fetch(`${API_URL}/api/followup/reminders/${reminderId}/dismiss`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setReminders(prev => prev.filter(r => r.id !== reminderId));
        toast.success('Reminder dismissed');
      }
    } catch (err) {
      toast.error('Error dismissing reminder');
    }
  };

  const completeReminder = async (reminderId, outreachType) => {
    try {
      const res = await fetch(`${API_URL}/api/followup/reminders/${reminderId}/complete?outreach_type=${outreachType}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setReminders(prev => prev.filter(r => r.id !== reminderId));
        toast.success('Marked as contacted');
        if (onRefresh) onRefresh();
      }
    } catch (err) {
      toast.error('Error updating reminder');
    }
  };

  const exportToCalendar = async (contactId) => {
    try {
      const res = await fetch(`${API_URL}/api/calendar/export/followup/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `followup-${contactId}.ics`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('Calendar event downloaded');
      }
    } catch (err) {
      toast.error('Error exporting to calendar');
    }
  };

  const triggerCheck = async () => {
    try {
      const res = await fetch(`${API_URL}/api/followup/trigger`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Follow-up check triggered');
        setTimeout(fetchData, 3000);
      }
    } catch (err) {
      toast.error('Error triggering check');
    }
  };

  // Helper to get method icon
  const getMethodIcon = (method) => {
    return METHOD_ICONS[method] || Target;
  };

  if (loading) {
    // Compact loading state for mobile
    if (isMobile) {
      return (
        <div className="bg-white rounded-2xl border border-slate-100 p-4 animate-pulse">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 bg-slate-200 rounded-lg" />
            <div className="h-4 w-32 bg-slate-200 rounded" />
          </div>
        </div>
      );
    }
    return (
      <div className="bg-white rounded-2xl border border-slate-100 p-6 animate-pulse">
        <div className="h-6 w-48 bg-slate-200 rounded mb-4" />
        <div className="space-y-3">
          <div className="h-16 bg-slate-100 rounded-xl" />
          <div className="h-16 bg-slate-100 rounded-xl" />
        </div>
      </div>
    );
  }

  const activeReminders = reminders.filter(r => !r.dismissed && !r.actioned);
  const aiEnabled = usageStats?.ai_enabled;
  const dailyCapExceeded = usageStats?.daily_cap_exceeded;
  const monthlyCapExceeded = usageStats?.monthly_cap_exceeded;
  
  // Calculate summary for collapsed mobile view
  const totalPending = activeReminders.length + staleContacts.length;
  const hasUrgent = activeReminders.some(r => r.recommendation?.urgency === 'high');
  
  // Mobile Collapsed State - compact summary card
  if (isMobile && !isExpanded) {
    return (
      <>
        <div 
          className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden cursor-pointer active:bg-slate-50 transition-colors"
          onClick={() => setIsExpanded(true)}
          data-testid="followup-panel-collapsed"
        >
          <div className="px-4 py-3 bg-gradient-to-r from-[#1e3a5f] to-[#2d4a6f] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-1.5 bg-white/20 rounded-lg">
                <Brain className="h-4 w-4 text-white" />
              </div>
              <div className="flex items-center gap-2">
                <h3 className="font-heading font-medium text-white text-sm">Follow-up Assistant</h3>
                {aiEnabled && <Sparkles className="h-3 w-3 text-amber-300" />}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {totalPending > 0 ? (
                <Badge 
                  variant="outline" 
                  className={`text-xs ${hasUrgent ? 'bg-red-500 text-white border-red-400' : 'bg-amber-500 text-white border-amber-400'}`}
                >
                  {totalPending} pending
                </Badge>
              ) : (
                <Badge variant="outline" className="bg-emerald-500 text-white border-emerald-400 text-xs">
                  All caught up
                </Badge>
              )}
              <ChevronDown className="h-4 w-4 text-white/70" />
            </div>
          </div>
        </div>
        
        {/* Settings Dialog - always available */}
        {settingsOpen && renderSettingsDialog()}
        {selectedContact && renderContactModal()}
      </>
    );
  }

  // Helper function to render settings dialog
  const renderSettingsDialog = () => (
    <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Follow-up AI Settings
          </DialogTitle>
          <DialogDescription>
            Configure AI recommendations for your team
          </DialogDescription>
        </DialogHeader>
        {/* Settings content will be rendered from the original location */}
      </DialogContent>
    </Dialog>
  );
  
  // Helper function to render contact modal
  const renderContactModal = () => null; // Placeholder, actual modal is rendered below

  return (
    <>
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        {/* Header - with collapse button on mobile */}
        <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-[#1e3a5f] to-[#2d4a6f] flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2 bg-white/20 rounded-lg">
              <Brain className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-white text-sm sm:text-base">Follow-up Assistant</h3>
              <p className="text-xs sm:text-sm text-white/70 hidden sm:block">
                {aiEnabled ? 'AI-powered recommendations' : 'Rule-based recommendations'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            {isDirector && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  fetchSettings();
                  setSettingsOpen(true);
                }}
                className="text-white/80 hover:text-white hover:bg-white/10 h-8 w-8 p-0 sm:h-auto sm:w-auto sm:p-2"
              >
                <Settings className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchData}
              className="text-white/80 hover:text-white hover:bg-white/10 h-8 w-8 p-0 sm:h-auto sm:w-auto sm:p-2"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
            {/* Mobile collapse button */}
            {isMobile && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpanded(false)}
                className="text-white/80 hover:text-white hover:bg-white/10 h-8 w-8 p-0"
                data-testid="followup-panel-collapse-btn"
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>

        {/* AI Status Bar */}
        {usageStats && (
          <div className={`px-6 py-2 border-b ${aiEnabled ? 'bg-emerald-50 border-emerald-100' : 'bg-slate-50 border-slate-100'}`}>
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                {aiEnabled ? (
                  <>
                    <Sparkles className="h-3 w-3 text-emerald-600" />
                    <span className="text-emerald-700">AI enabled</span>
                    {(dailyCapExceeded || monthlyCapExceeded) && (
                      <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-200 text-xs">
                        Cap exceeded - using rule-based
                      </Badge>
                    )}
                  </>
                ) : (
                  <>
                    <Shield className="h-3 w-3 text-slate-500" />
                    <span className="text-slate-600">AI disabled (rule-based mode)</span>
                  </>
                )}
              </div>
              {aiEnabled && (
                <span className="text-slate-500">
                  {usageStats.calls_today}/{usageStats.daily_cap} today • 
                  £{usageStats.estimated_cost_month_gbp?.toFixed(2)} this month
                </span>
              )}
            </div>
          </div>
        )}

        {/* Stats Bar */}
        {staleContacts.length > 0 && (
          <div className="px-6 py-3 bg-amber-50 border-b border-amber-100 flex items-center justify-between">
            <div className="flex items-center gap-2 text-amber-800">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm font-medium">
                {staleContacts.length} contacts need follow-up
              </span>
            </div>
            {isDirector && (
              <Button
                variant="ghost"
                size="sm"
                onClick={triggerCheck}
                className="text-amber-700 hover:text-amber-900 hover:bg-amber-100"
              >
                <Zap className="h-4 w-4 mr-1" />
                Run Check
              </Button>
            )}
          </div>
        )}

        {/* Reminders List */}
        <div className="p-4 space-y-3 max-h-[400px] overflow-y-auto">
          {activeReminders.length === 0 && staleContacts.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-emerald-400" />
              <p className="font-medium">All caught up!</p>
              <p className="text-sm">No pending follow-ups</p>
            </div>
          ) : (
            <>
              {/* Active Reminders */}
              {activeReminders.map(reminder => {
                const rec = reminder.recommendation || {};
                const urgency = rec.urgency || 'low';
                return (
                  <div
                    key={reminder.id}
                    className="p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-slate-900 truncate">
                            {reminder.contact_name}
                          </span>
                          <Badge variant="outline" className={`${URGENCY_COLORS[urgency]} text-white text-xs`}>
                            {urgency.toUpperCase()}
                          </Badge>
                          {rec.ai_generated && (
                            <Sparkles className="h-3 w-3 text-amber-500" title="AI-generated" />
                          )}
                        </div>
                        <p className="text-sm text-slate-600 truncate">
                          {reminder.company_name}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {reminder.days_since_contact} days ago
                          </span>
                          {rec.recommended_method && (
                            <span className="flex items-center gap-1">
                              {(() => {
                                const Icon = getMethodIcon(rec.recommended_method);
                                return <Icon className="h-4 w-4" />;
                              })()}
                              {rec.recommended_method}
                            </span>
                          )}
                        </div>
                        {rec.suggested_approach && (
                          <p className="text-xs text-slate-600 mt-2 line-clamp-2">
                            {rec.suggested_approach}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col gap-1">
                        <Button
                          size="sm"
                          onClick={() => completeReminder(reminder.id, rec.recommended_method)}
                          className="bg-emerald-500 hover:bg-emerald-600 text-xs"
                        >
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Done
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => exportToCalendar(reminder.contact_id)}
                          className="text-xs"
                        >
                          <Download className="h-3 w-3 mr-1" />
                          .ics
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => dismissReminder(reminder.id)}
                          className="text-slate-400 hover:text-slate-600 text-xs"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Stale Contacts (if no reminders generated yet) */}
              {activeReminders.length === 0 && staleContacts.slice(0, 5).map(contact => (
                <div
                  key={contact.id}
                  className="p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer"
                  onClick={() => getRecommendation(contact.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 truncate">
                        {contact.contact_name}
                      </p>
                      <p className="text-sm text-slate-600 truncate">
                        {contact.company_name}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        <Clock className="h-3 w-3 inline mr-1" />
                        {contact.days_since_contact} days since last contact
                      </p>
                    </div>
                    <Button variant="ghost" size="sm" disabled={loadingRec}>
                      <Brain className="h-4 w-4 mr-1" />
                      Get Tip
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Disclaimer */}
        <div className="px-4 py-2 bg-slate-50 border-t text-center">
          <p className="text-xs text-slate-400 flex items-center justify-center gap-1">
            <Info className="h-3 w-3" />
            Suggestions are guidance only. Always apply professional judgement.
          </p>
        </div>
      </div>

      {/* AI Recommendation Modal */}
      <Dialog open={!!recommendation} onOpenChange={() => setRecommendation(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-[#1e3a5f]" />
              Follow-up Recommendation
            </DialogTitle>
            <DialogDescription>
              {recommendation?.recommendation?.ai_generated 
                ? 'AI-powered suggestion' 
                : 'Rule-based suggestion'}
            </DialogDescription>
          </DialogHeader>

          {recommendation && (
            <div className="space-y-4">
              {/* Contact Info */}
              <div className="p-4 bg-slate-50 rounded-xl">
                <h4 className="font-semibold text-slate-900">{selectedContact?.contact_name}</h4>
                <p className="text-sm text-slate-600">{selectedContact?.company_name}</p>
                <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                  <span className="capitalize">{selectedContact?.relationship_status}</span>
                  <span>{selectedContact?.days_since_contact} days since contact</span>
                </div>
              </div>

              {/* Recommendation */}
              <div className="space-y-3">
                <div className="flex items-center gap-4">
                  <div className="flex-1 p-3 bg-[#1e3a5f]/5 rounded-xl text-center">
                    <p className="text-xs text-slate-500 mb-1">Method</p>
                    <p className="font-semibold text-[#1e3a5f] capitalize">
                      {recommendation.recommendation?.recommended_method}
                    </p>
                  </div>
                  <div className="flex-1 p-3 bg-[#1e3a5f]/5 rounded-xl text-center">
                    <p className="text-xs text-slate-500 mb-1">Urgency</p>
                    <Badge className={`${URGENCY_COLORS[recommendation.recommendation?.urgency]} text-white`}>
                      {recommendation.recommendation?.urgency?.toUpperCase()}
                    </Badge>
                  </div>
                  <div className="flex-1 p-3 bg-[#1e3a5f]/5 rounded-xl text-center">
                    <p className="text-xs text-slate-500 mb-1">Best Time</p>
                    <p className="font-semibold text-[#1e3a5f] capitalize">
                      {recommendation.recommendation?.best_time}
                    </p>
                  </div>
                </div>

                <div className="p-4 bg-amber-50 rounded-xl border border-amber-100">
                  <p className="text-sm text-amber-900">
                    {recommendation.recommendation?.ai_generated && (
                      <Sparkles className="h-4 w-4 inline mr-2 text-amber-500" />
                    )}
                    {recommendation.recommendation?.suggested_approach}
                  </p>
                </div>

                {recommendation.recommendation?.key_points?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 mb-2">Key Points</p>
                    <ul className="space-y-1">
                      {recommendation.recommendation.key_points.map((point, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                          <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {recommendation.recommendation?.avoid && (
                  <div className="p-3 bg-red-50 rounded-lg border border-red-100">
                    <p className="text-xs text-red-700">
                      <AlertTriangle className="h-3 w-3 inline mr-1" />
                      Avoid: {recommendation.recommendation.avoid}
                    </p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t">
                <Button
                  onClick={() => exportToCalendar(selectedContact?.id)}
                  variant="outline"
                  className="flex-1"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Add to Calendar
                </Button>
                <Button
                  onClick={() => setRecommendation(null)}
                  className="flex-1 bg-[#1e3a5f] hover:bg-[#162c46]"
                >
                  Got it
                </Button>
              </div>

              {/* Disclaimer */}
              <p className="text-xs text-center text-slate-400 flex items-center justify-center gap-1">
                <Info className="h-3 w-3" />
                {recommendation.recommendation?.ai_disclaimer || 'Suggestions are guidance only.'}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Settings Modal */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Follow-up Reminder Settings
            </DialogTitle>
          </DialogHeader>

          {settings && (
            <div className="space-y-4">
              {/* AI Status */}
              {usageStats && (
                <div className={`p-3 rounded-lg ${usageStats.ai_enabled ? 'bg-emerald-50' : 'bg-slate-50'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">AI Status</span>
                    <Badge variant={usageStats.ai_enabled ? 'default' : 'secondary'}>
                      {usageStats.ai_enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </div>
                  {usageStats.ai_enabled && (
                    <>
                      <div className="text-xs text-slate-600 space-y-1">
                        <div className="flex justify-between">
                          <span>Today: {usageStats.calls_today}/{usageStats.daily_cap}</span>
                          <span>Month: {usageStats.calls_this_month}/{usageStats.monthly_cap}</span>
                        </div>
                        <Progress 
                          value={(usageStats.calls_today / usageStats.daily_cap) * 100} 
                          className="h-1"
                        />
                        <div className="flex justify-between text-slate-500">
                          <span>Est. cost today: £{usageStats.estimated_cost_today_gbp?.toFixed(2)}</span>
                          <span>Month: £{usageStats.estimated_cost_month_gbp?.toFixed(2)}</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="flex items-center justify-between">
                <div>
                  <Label>Enable Automated Reminders</Label>
                  <p className="text-xs text-slate-500">Daily check for stale contacts</p>
                </div>
                <Switch
                  checked={settings.enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Enable AI Recommendations</Label>
                  <p className="text-xs text-slate-500">GPT-4o powered suggestions (opt-in)</p>
                </div>
                <Switch
                  checked={settings.ai_enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, ai_enabled: checked })}
                />
              </div>

              <div>
                <Label>Days Without Contact</Label>
                <p className="text-xs text-slate-500 mb-2">Trigger reminder after X days</p>
                <Input
                  type="number"
                  min="1"
                  max="90"
                  value={settings.stale_days_threshold}
                  onChange={(e) => setSettings({ ...settings, stale_days_threshold: parseInt(e.target.value) })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>AI Daily Cap</Label>
                  <Input
                    type="number"
                    min="1"
                    max="500"
                    value={settings.ai_max_calls_per_day}
                    onChange={(e) => setSettings({ ...settings, ai_max_calls_per_day: parseInt(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>AI Monthly Cap</Label>
                  <Input
                    type="number"
                    min="10"
                    max="5000"
                    value={settings.ai_max_calls_per_month}
                    onChange={(e) => setSettings({ ...settings, ai_max_calls_per_month: parseInt(e.target.value) })}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label>Email Notifications</Label>
                  <p className="text-xs text-slate-500">Send reminders via email</p>
                </div>
                <Switch
                  checked={settings.notify_by_email}
                  onCheckedChange={(checked) => setSettings({ ...settings, notify_by_email: checked })}
                />
              </div>

              {/* GDPR Note */}
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100 text-xs text-blue-700">
                <Shield className="h-3 w-3 inline mr-1" />
                <strong>Data Privacy:</strong> AI only receives contact name, company, sector, status, and days since contact. 
                No emails, phones, addresses, or confidential notes are sent to the AI provider.
              </div>

              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setSettingsOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveSettings}
                  disabled={savingSettings}
                  className="flex-1 bg-[#1e3a5f] hover:bg-[#162c46]"
                >
                  {savingSettings ? 'Saving...' : 'Save Settings'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FollowUpRemindersPanel;
